import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { defaultOrganizationName, useAuth } from '../auth/AuthProvider';
import { IntervalToggle } from './PlanCards';
import { CheckIcon, XIcon } from './icons';
import { Button, ErrorText, cx } from './ui';
import { track } from '../lib/amplitude';
import { errorMessage } from '../lib/api';
import { dollars } from '../lib/format';
import { FREE_LIMIT } from '../lib/plans';
import type { BillingInterval, Pricing } from '../lib/types';
import { createOrg, seatPriceLabel, trialLabel, yearlySavingsPercent } from '../queries/orgs';

/**
 * The Subscription tab when there is nothing to manage yet: the offer, laid
 * out to be read rather than filed. One price, one toggle, one field, one
 * button, and a plain account of what changes against Free. It is the
 * purchase page; it just lives where the plan will live afterwards.
 */

const ROWS: Array<{ label: string; free: string | boolean; team: string | boolean }> = [
  { label: 'Recordings that stay playable', free: `Your ${FREE_LIMIT} most recent`, team: 'All of them' },
  { label: 'Captions and searchable transcripts', free: false, team: true },
  { label: 'Cut, trim and correct recordings', free: false, team: true },
  { label: 'A shared library for the whole team', free: false, team: true },
  { label: 'Owners and admins who see everything', free: false, team: true },
  { label: 'Free seats for the rest of the team, on the free plan', free: false, team: 'Unlimited' },
  { label: 'Share links, comments, reactions', free: true, team: true },
  { label: 'The Mac recorder', free: true, team: true },
];

function Cell({ value, strong = false }: { value: string | boolean; strong?: boolean }) {
  if (value === true) return <CheckIcon size={16} className={strong ? 'text-go' : 'text-chalk-dim'} />;
  if (value === false) return <XIcon size={14} className="text-chalk-faint/60" />;
  return <span className={cx('text-caption', strong ? 'text-chalk' : 'text-chalk-dim')}>{value}</span>;
}

export function UpgradePitch({
  pricing,
  onCreated,
}: {
  pricing: Pricing | null;
  onCreated: (org: { id: string; slug: string }) => void;
}) {
  const [interval, setInterval] = useState<BillingInterval>('year');
  const [seats, setSeats] = useState(3);
  const { user } = useAuth();
  const [name, setName] = useState(() => defaultOrganizationName(user));
  const [invalid, setInvalid] = useState<string | null>(null);

  const price = seatPriceLabel(pricing, interval);
  const perSeatCents = pricing
    ? interval === 'year' && pricing.seatYearlyCents !== null
      ? pricing.seatYearlyCents
      : pricing.seatMonthlyCents
    : interval === 'year'
      ? 9000
      : 900;
  const savings = yearlySavingsPercent(pricing);
  const trial = trialLabel(pricing, interval);

  const create = useMutation({
    mutationFn: (value: string) => createOrg(value, interval),
    onSuccess: (response) => {
      // A team starts on one paid seat, the owner's; the calculator above is
      // an estimate, not what is bought here.
      track('Team Created', { interval, seats: 1 });
      if (response.checkoutUrl) {
        track('Checkout Started', { interval, seats: 1, value: perSeatCents / 100 });
        window.location.assign(response.checkoutUrl);
        return;
      }
      onCreated(response.org);
    },
  });

  return (
    <div className="animate-fade-up">
      {/* The pitch */}
      <div className="max-w-2xl">
        <p className="text-micro uppercase tracking-[0.12em] text-signal">Upgrade</p>
        <h2 className="mt-2 text-h1 text-chalk">Everything you record, kept and transcribed.</h2>
        <p className="mt-3 text-prose text-chalk-dim">
          Free keeps your {FREE_LIMIT} most recent recordings. Team keeps all of them, writes the
          transcript, and gives the people you work with one place to find what you made. Half the
          price of Loom, whichever way you pay.
        </p>
      </div>

      <div className="mt-8 grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] lg:items-start">
        {/* The offer */}
        <section className="relative overflow-hidden rounded-md border border-signal/40 bg-slate-900 p-6 shadow-raised">
          <div aria-hidden className="clapper-stripes absolute inset-x-0 top-0 h-[3px] opacity-[0.18]" />
          <div
            aria-hidden
            className="pointer-events-none absolute -right-24 -top-24 h-64 w-64 rounded-full bg-signal/10 blur-3xl"
          />

          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h3 className="text-h3 text-chalk">Team</h3>
              <p className="mt-0.5 text-caption text-chalk-dim">Per paid seat. Free seats cost nothing.</p>
            </div>
            <IntervalToggle pricing={pricing} value={interval} onChange={setInterval} />
          </div>

          <p className="mt-6 flex items-baseline gap-2">
            <span className="text-display text-chalk tnum">{price.price}</span>
            <span className="text-body text-chalk-dim">{price.cadence}</span>
          </p>
          <p className="mt-1 h-5 text-caption text-go">
            {trial
              ? `${trial}. Nothing charged until it ends.`
              : savings > 0
                ? `Pay yearly and save ${savings}%, with a free first week.`
                : ''}
          </p>

          {/* Seat estimate: the question everyone does in their head */}
          <div className="mt-6 flex flex-wrap items-center gap-3 rounded border border-hairline bg-slate-950/60 px-4 py-3">
            <span className="text-caption text-chalk-dim">For a team of</span>
            <span className="inline-flex items-center rounded border border-hairline">
              <button
                type="button"
                onClick={() => setSeats((n) => Math.max(1, n - 1))}
                aria-label="Fewer seats"
                className="h-8 w-8 text-chalk-dim hover:bg-slate-800 hover:text-chalk"
              >
                −
              </button>
              <span className="w-8 text-center text-body text-chalk tnum">{seats}</span>
              <button
                type="button"
                onClick={() => setSeats((n) => Math.min(200, n + 1))}
                aria-label="More seats"
                className="h-8 w-8 text-chalk-dim hover:bg-slate-800 hover:text-chalk"
              >
                +
              </button>
            </span>
            <span className="ml-auto text-body text-chalk tnum">
              {dollars(perSeatCents * seats)}
              <span className="text-chalk-faint"> a {interval}</span>
            </span>
          </div>

          <form
            className="mt-6 space-y-3"
            onSubmit={(event) => {
              event.preventDefault();
              setInvalid(null);
              const trimmed = name.trim();
              if (trimmed.length < 2) {
                setInvalid('Give the organization a name, usually your company or group.');
                return;
              }
              create.mutate(trimmed);
            }}
          >
            <label className="block">
              <span className="block text-caption font-medium text-chalk-dim">Organization name</span>
              <input
                value={name}
                maxLength={80}
                placeholder="Northwind Design"
                onChange={(event) => setName(event.target.value)}
                className="mt-1.5 h-11 w-full rounded border border-hairline bg-slate-950 px-3 text-body text-chalk placeholder:text-chalk-faint focus:border-signal focus:outline-none"
              />
            </label>
            {invalid ? <ErrorText>{invalid}</ErrorText> : null}
            {create.isError ? <ErrorText>{errorMessage(create.error)}</ErrorText> : null}
            <Button type="submit" variant="primary" size="lg" className="w-full" busy={create.isPending}>
              {trial ? 'Start the free week' : 'Continue to checkout'}
            </Button>
            <p className="text-center text-caption text-chalk-faint">
              {pricing?.enabled ? 'Secure checkout with Stripe. Cancel any time.' : 'No card needed on this deployment.'}
            </p>
          </form>
        </section>

        {/* What changes */}
        <section className="rounded-md border border-hairline bg-slate-900/50">
          <div className="grid grid-cols-[minmax(0,1fr)_5.5rem_5.5rem] items-center gap-3 border-b border-hairline px-5 py-3 text-caption text-chalk-faint">
            <span>What changes</span>
            <span className="text-center">Free</span>
            <span className="text-center text-chalk">Team</span>
          </div>
          <ul>
            {ROWS.map((row) => (
              <li
                key={row.label}
                className="grid grid-cols-[minmax(0,1fr)_5.5rem_5.5rem] items-center gap-3 border-b border-hairline px-5 py-3 last:border-b-0"
              >
                <span className="text-body text-chalk">{row.label}</span>
                <span className="flex justify-center">
                  <Cell value={row.free} />
                </span>
                <span className="flex justify-center">
                  <Cell value={row.team} strong />
                </span>
              </li>
            ))}
          </ul>
        </section>
      </div>
    </div>
  );
}
